import { useContext, useMemo } from 'react';
import Attachments from 'src/components/AppealForm/Attachments';
import BusinessOwnerInfo from 'src/components/AppealForm/BusinessOwnerInfo';
import Qualifications from 'src/components/AppealForm/Qualifications/index';
import Step from 'src/components/Stepper/Step';
import Confirmation from 'src/components/util/Confirmation';
import Page from 'src/components/util/Page';
import FormContext from 'src/contexts/appeal-context';
import appealAttachmentSchema from 'src/schemas/appealAttachmentSchema';
import businessOwnerInfoSchema from 'src/schemas/businessOwnerInfoSchema';
import qualificationSchema from 'src/schemas/qualificationSchema';

/**
 * A form with multiple steps for submitting a property appeal.
 * It displays a confirmation message when the form has been successfully submitted.
 *
 * @returns {JSX.Element} The AppealForm component.
 */
export default function AppealForm() {
  const { formData, isComplete, onBack, onNext, onSubmit, isLoading, contacts, disableNext } =
    useContext(FormContext);
  const formSteps = useMemo(
    () => [
      {
        component: <Qualifications />,
        label: 'Qualifications',
        schema: qualificationSchema,
        isFirstStep: true,
        initialValues: {
          causedByCountyError: formData.causedByCountyError,
          paidInFull: formData.paidInFull,
          doesNotExceedPenalty: formData.doesNotExceedPenalty,
          assignedBecauseOfFailure: formData.assignedBecauseOfFailure,
          noPenaltyUnderUCA: formData.noPenaltyUnderUCA,
          filedDelinquentStatement: formData.filedDelinquentStatement,
        },
      },
      {
        component: <BusinessOwnerInfo />,
        label: 'Business / Owner Information',
        schema: businessOwnerInfoSchema,
        initialValues: {
          business: formData.business,
        },
        isLastStep: false,
      },
      {
        component: <Attachments />,
        label: 'File Upload',
        schema: appealAttachmentSchema,
        initialValues: {
          scheduleB: formData.scheduleB,
        },
        isLastStep: true,
      },
    ],
    [formData]
  );

  if (isComplete) {
    return (
      <Confirmation
        text="Your Property Appeal Form has successfully been submitted!"
        contacts={contacts}
      />
    );
  }

  return (
    <Page pageContext={FormContext}>
      {formSteps.map((step) => (
        <Step
          key={step.label}
          {...step}
          disableNext={disableNext}
          onBack={onBack}
          onNext={onNext}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      ))}
    </Page>
  );
}
